import LogoutSlidingPanel from "@components/sliders/LogoutSlidingPanel";
import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import LogoImageComponent from "src/modules/authentication/components/LogoImageComponent";
import { NavSection } from "./navSections";

export default function MobileLayout({ sections }: { sections: NavSection[] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const navigateTo = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-bg-page-body">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-border-subtle bg-[#FFFFFF] px-3">
        <button
          type="button"
          aria-label="Open navigation"
          className="flex h-10 w-10 items-center justify-center rounded-lg text-[#3A4158] hover:bg-[#F4F6F8]"
          onClick={() => setDrawerOpen(true)}
        >
          <span className="material-symbols-outlined text-[24px]">menu</span>
        </button>
        <LogoImageComponent className="h-9 w-auto max-w-[110px]" />
        <button
          type="button"
          aria-label="Logout"
          className="flex h-10 w-10 items-center justify-center rounded-lg text-[#3A4158] hover:bg-[#F4F6F8]"
          onClick={() => setLogoutOpen(true)}
        >
          <span className="material-symbols-outlined text-[20px]">logout</span>
        </button>
      </header>

      {drawerOpen && (
        <button
          type="button"
          aria-label="Close navigation overlay"
          className="fixed inset-0 z-40 bg-[#15182C]/50"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-[280px] max-w-[85vw] flex-col border-r border-border-subtle bg-[#FFFFFF] shadow-xl transition-transform duration-300 ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-14 items-center justify-between border-b border-border-subtle px-4">
          <LogoImageComponent className="h-9 w-auto max-w-[120px]" />
          <button
            type="button"
            aria-label="Close navigation"
            className="flex h-9 w-9 items-center justify-center rounded-lg"
            onClick={() => setDrawerOpen(false)}
          >
            <span className="material-symbols-outlined text-[22px]">close</span>
          </button>
        </div>
        <nav className="flex flex-1 flex-col gap-4 overflow-y-auto p-3">
          {sections.map((section, sectionIndex) => (
            <div key={section.title} className="flex flex-col gap-1.5">
              <span className="px-2.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-subtle">
                {section.title}
              </span>
              <div className="flex flex-col gap-0.5">
                {section.items.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      type="button"
                      onClick={() => navigateTo(item.path)}
                      className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium transition-colors ${
                        active
                          ? "bg-[#E8F6FC] text-[#2436C7]"
                          : "text-[#3A4158] hover:bg-[#F4F6F8]"
                      }`}
                    >
                      <span className="material-symbols-outlined text-[18px] leading-none">
                        {item.icon}
                      </span>
                      {item.label}
                    </button>
                  );
                })}
              </div>
              {sectionIndex < sections.length - 1 && (
                <div className="mx-2 mt-2 h-px bg-border-subtle" />
              )}
            </div>
          ))}
        </nav>
      </aside>

      <main className="min-h-0 flex-1 overflow-auto">
        <Outlet />
      </main>

      <LogoutSlidingPanel
        isOpen={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        type="send"
        onClickConfirm={() => {
          secureLocalStorage.clear();
          navigate("/login");
        }}
        sliderHeading="Logout"
      />
    </div>
  );
}
